import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  Box,
  Button,
  Collapse,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

// "Process Confirmations" in the Appointment confirmations hub: the dry-run
// preview of applying patients' C/R texts to their appointments, then — after
// Apply — the record of what changed. Rows are grouped under the three counts
// on their own tabs so it is plain which appointment falls under which:
//   To update             everything that will change (or did)
//   Set by hand → system  the subset staff had set by hand (the text wins)
//   Left as is            untouched, each with its reason
// `view` is utils/confirmationScheduling.buildReconcileView(report).

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const RULES = [
  "Each appointment takes the patient's latest C or R text from the last 30 days, whatever order the messages were logged in.",
  "That answer replaces the current status, including a status staff set by hand.",
  "Left as is: appointments already at the patient's answer, texts that are not a plain C or R, and appointments with no reply or no phone number.",
];

const Cell = (props) => (
  <Td fontSize="sm" py={2} px={3} verticalAlign="top" whiteSpace="normal" wordBreak="break-word" {...props} />
);
const Head = (props) => <Th fontSize="xs" py={2} px={3} textTransform="none" letterSpacing="normal" {...props} />;

const RowsTable = ({ testId, columns, rows, empty, renderRow }) => {
  if (!rows.length) {
    return (
      <Text fontSize="sm" color="gray.500" fontStyle="italic" py={2} data-testid={testId}>
        {empty}
      </Text>
    );
  }
  return (
    <Box borderWidth="1px" borderRadius="md" overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>{columns.map((c) => <Head key={c}>{c}</Head>)}</Tr>
        </Thead>
        <Tbody data-testid={testId}>
          {rows.map((r) => (
            <Tr key={r.key} data-testid="row">{renderRow(r)}</Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const CountTab = ({ label, count, colorScheme }) => (
  <Tab fontSize="sm" whiteSpace="nowrap">
    {label}
    <Badge ml={2} colorScheme={colorScheme} borderRadius="full" px={2}>{count}</Badge>
  </Tab>
);

const ProcessConfirmationsDialog = ({ isOpen, onClose, loading, error, view, isApplying, onApply }) => {
  const [showRules, setShowRules] = useState(false);
  const done = Boolean(view?.done);
  const multi = (view?.clinics || []).length > 1;
  const clinicCell = (r) => (multi ? <Cell>{r.clinic}</Cell> : null);
  const withClinic = (cols) => (multi ? [cols[0], "Clinic", ...cols.slice(1)] : cols);

  return (
    <Modal isOpen={isOpen} onClose={() => !isApplying && onClose()} size="3xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader pb={2}>{done ? "Confirmations processed" : "Process confirmations"}</ModalHeader>
        <ModalCloseButton isDisabled={isApplying} />
        <ModalBody>
          {loading ? (
            <HStack py={6} justify="center" data-testid="process-confirmations-loading">
              <Spinner size="sm" />
              <Text fontSize="sm">Checking the message history…</Text>
            </HStack>
          ) : error ? (
            <Alert status="error" borderRadius="md" data-testid="process-confirmations-error">
              <AlertIcon />
              <AlertDescription fontSize="sm">{error}</AlertDescription>
            </Alert>
          ) : view ? (
            <VStack align="stretch" spacing={4}>
              <Box data-testid="process-confirmations-summary">
                <Text fontSize="sm">
                  Checked <b>{plural(view.appointments, "appointment")}</b> {view.dateText}
                </Text>
                <Wrap mt={2} spacing={2}>
                  {view.clinics.map((c) => (
                    <WrapItem key={c.name}>
                      <Tag size="sm" variant="subtle" colorScheme="gray">
                        {c.name} {c.count ?? "—"}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              {done && (
                <Text fontSize="sm" fontWeight="semibold" data-testid="process-confirmations-result">
                  Updated {plural(view.counts.update, "appointment")}:{" "}
                  {plural(view.counts.statusChanges, "status change")}, {view.counts.manual} switched from
                  hand-set to system.
                </Text>
              )}

              {view.warning && (
                <Alert status="warning" borderRadius="md" py={2} data-testid="process-confirmations-warning">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">{view.warning}</AlertDescription>
                </Alert>
              )}

              {view.errors.length > 0 && (
                <Alert status="error" borderRadius="md" py={2} data-testid="process-confirmations-errors">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Couldn't check: {view.errors.map((e) => `${e.schema} (${e.error})`).join("; ")}
                  </AlertDescription>
                </Alert>
              )}

              <Tabs variant="soft-rounded" colorScheme="purple" size="sm">
                <TabList flexWrap="wrap" gap={2}>
                  <CountTab label="To update" count={view.counts.update} colorScheme="green" />
                  <CountTab label="Set by hand → system" count={view.counts.manual} colorScheme="red" />
                  <CountTab label="Left as is" count={view.counts.kept} colorScheme="gray" />
                </TabList>
                <TabPanels>
                  <TabPanel px={0} pb={0}>
                    <RowsTable
                      testId="rows-update"
                      columns={withClinic(["Patient", "Date", "Change", "Patient replied"])}
                      rows={view.update}
                      empty={done ? "Nothing was changed." : "Nothing to update — every appointment already matches the patient's texts."}
                      renderRow={(r) => (
                        <>
                          <Cell fontWeight="medium">{r.patient}</Cell>
                          {clinicCell(r)}
                          <Cell whiteSpace="nowrap">{r.date}</Cell>
                          <Cell>
                            <Text fontWeight="semibold">{r.change}</Text>
                            {r.manual && (
                              <Tag size="sm" colorScheme="red" mt={1}>set by hand</Tag>
                            )}
                          </Cell>
                          <Cell>
                            <Text>&ldquo;{r.reply}&rdquo; · {r.replyAt}</Text>
                            {r.note && <Text fontSize="xs" color="gray.500" mt={1}>{r.note}</Text>}
                          </Cell>
                        </>
                      )}
                    />
                  </TabPanel>
                  <TabPanel px={0} pb={0}>
                    <RowsTable
                      testId="rows-manual"
                      columns={withClinic(["Patient", "Date", "Change", "Set by hand by"])}
                      rows={view.manual}
                      empty="None of these appointments were set by hand."
                      renderRow={(r) => (
                        <>
                          <Cell fontWeight="medium">{r.patient}</Cell>
                          {clinicCell(r)}
                          <Cell whiteSpace="nowrap">{r.date}</Cell>
                          <Cell fontWeight="semibold">{r.change}</Cell>
                          <Cell>
                            <Text>{r.setBy || "—"}</Text>
                            {r.setAt && <Text fontSize="xs" color="gray.500">{r.setAt}</Text>}
                          </Cell>
                        </>
                      )}
                    />
                  </TabPanel>
                  <TabPanel px={0} pb={0}>
                    <RowsTable
                      testId="rows-kept"
                      columns={withClinic(["Patient", "Date", "Why"])}
                      rows={view.kept}
                      empty="Nothing was left out."
                      renderRow={(r) => (
                        <>
                          <Cell fontWeight="medium">{r.patient}</Cell>
                          {clinicCell(r)}
                          <Cell whiteSpace="nowrap">{r.date}</Cell>
                          <Cell color="gray.600">{r.reason}</Cell>
                        </>
                      )}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>

              <Box>
                <Button variant="link" size="xs" colorScheme="purple" onClick={() => setShowRules((v) => !v)}>
                  {showRules ? "Hide how this is decided" : "How is this decided?"}
                </Button>
                <Collapse in={showRules} animateOpacity>
                  <VStack align="stretch" spacing={1} mt={2}>
                    {RULES.map((line) => (
                      <Text key={line} fontSize="xs" color="gray.600">{line}</Text>
                    ))}
                  </VStack>
                </Collapse>
              </Box>
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter gap={3} flexWrap="wrap">
          <Text fontSize="xs" color="gray.500" flex="1" minW="180px">
            {done ? "Saved. The schedule has been refreshed." : "Nothing is saved until you click Apply."}
          </Text>
          {done ? (
            <Button onClick={onClose} data-testid="process-confirmations-close">Close</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} isDisabled={isApplying}>Cancel</Button>
              <Button
                colorScheme="purple"
                onClick={onApply}
                isLoading={isApplying}
                loadingText="Applying…"
                isDisabled={loading || Boolean(error) || !view}
                data-testid="process-confirmations-apply"
              >
                Apply
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProcessConfirmationsDialog;
